import type { ReactNode } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';

// SaveStateIndicator — the shared save-state renderer (P10 §2, story 2-141).
// Not in the §2.2/§2.4 table by name — the story text says so explicitly —
// but built to the SAME "presentation only, caller supplies the real state"
// discipline every table primitive follows, and to the SAME live-region
// contract `ProgressMeter`'s own header comment argues for a bar's value:
// the a11y wiring lives here, once, instead of being re-typed at each call
// site (which is exactly what happened before this file existed — see below).
//
// ── Extracted from two real, independently-evolved consumers ──────────────
// `TemplateEditorPage.tsx` (story 4-2d) and `CaptureHeader.tsx` (stories
// 5-10b/5-10d) each hand-rolled an almost-identical block: a
// `role="status" aria-live="polite" data-testid="…" data-state={kind}`
// wrapper switching on a `state.kind` union to pick the sentence and the
// tone. They drifted from each other in exactly the ways two independent
// implementations of one idea always do — different `data-testid` names,
// `TemplateEditorPage` using `text-amber-600 dark:text-amber-400` where
// `CaptureHeader` used the `text-warning` semantic token for the identical
// concept, `TemplateEditorPage` never having a `queued` state to render at all. This component is that block, written once: the ARIA contract, the
// per-kind icon (WCAG 1.4.1 — see below) and the per-kind tone token live
// HERE; the copy — which differs legitimately (a template names a revision
// number, an inspection answer names a change count) — stays 100% with the
// caller, passed as `children`.
//
// ── Why `offline` is not a value this component (or either real caller)
//    can ever hold — not merely a value nobody happened to pass ─────────────
// Backlog finding 5-29: the release cut has no outbox, so "genuinely offline
// and syncing in the background" is not a real product state yet. Both real
// autosave hooks were grepped tree-wide before writing this file's type:
//   `useTemplateAutosave.ts`  → SaveState = idle | saving | saved | failed | conflict
//   `useAnswerAutosave.ts`    → SaveState = idle | saving | saved | queued | conflict | failed
// Neither union has ever had an `offline` member — `useAnswerAutosave`'s own
// header comment names the state `queued` and is explicit about why: "the
// ops are held, not synced… deliberately explicit that DefectSnap is not fully
// offline yet." `SaveStateKind` below is the union of those two real unions
// and nothing more. There is no cast, no `as SaveStateKind`, no fallback
// branch that could smuggle a stringly-typed `'offline'` through — the type
// checker refuses a value TypeScript cannot even name, which is what makes
// this "structurally unreachable" rather than "not currently produced": the
// day a real offline outbox ships, it earns a new named kind (`recovering`,
// `syncing`, whatever the real product state is) exactly the way `queued`
// did, never a silent rename of this file's existing promise.
export type SaveStateKind = 'idle' | 'saving' | 'saved' | 'queued' | 'conflict' | 'failed';

const TONE_CLASS: Record<SaveStateKind, string> = {
  idle: 'text-muted-foreground',
  saving: 'text-muted-foreground',
  saved: 'text-muted-foreground',
  queued: 'text-warning',
  conflict: 'text-warning',
  failed: 'text-destructive',
};

// One glyph per kind, fixed (WCAG 1.4.1 — the same discipline `StatusChip`
// applies to lifecycle tone): `saving` spins (respecting `prefers-reduced-
// motion` via the shared `motion-reduce:animate-none` utility Tailwind ships
// with no token of its own to name), the rest are static shapes distinct
// enough to read in grayscale.
const ICON: Partial<Record<SaveStateKind, typeof CheckCircleIcon>> = {
  saving: ArrowPathIcon,
  saved: CheckCircleIcon,
  queued: ClockIcon,
  conflict: ExclamationTriangleIcon,
  failed: XCircleIcon,
};

export interface SaveStateIndicatorProps {
  kind: SaveStateKind;
  /**
   * The caller-composed message for THIS kind — revision numbers, change
   * counts and server sentences differ per consumer, so no copy is baked in
   * here. `idle` is the one kind allowed to render nothing (an untouched
   * editor has nothing to announce); every other kind should pass real text.
   */
  children?: ReactNode;
  className?: string;
  'data-testid'?: string;
  /** Native title passthrough — e.g. the server's exact acknowledgement timestamp behind a relative "Saved 2m ago". */
  title?: string;
}

export function SaveStateIndicator({
  kind,
  children,
  className = '',
  'data-testid': testId,
  title,
}: SaveStateIndicatorProps): JSX.Element {
  const Icon = ICON[kind];
  return (
    <span
      role="status"
      aria-live="polite"
      data-state={kind}
      data-testid={testId}
      title={title}
      className={`inline-flex items-center gap-1.5 text-xs font-medium ${TONE_CLASS[kind]} ${className}`}
    >
      {Icon && (
        <Icon className={`h-3.5 w-3.5 shrink-0 ${kind === 'saving' ? 'animate-spin motion-reduce:animate-none' : ''}`} aria-hidden />
      )}
      {children}
    </span>
  );
}
